import React from 'react';
import { Button, Progress, Space, Steps, Tag, Tooltip, Typography } from 'antd';
import { LockOutlined, ReloadOutlined, UnlockOutlined } from '@ant-design/icons';
import type { ClosingPackageProjection } from '../types';
import { closingPackageWorkflowStateMeta } from '../lib/workflowLabels';
import {
  CLOSING_PACKAGE_PROGRESS_STEPS,
  resolveClosingPackageWorkflowProgress,
} from '../lib/workflowProgress';
import './closingPackageWorkspace.css';

const { Text } = Typography;

export type ClosingPackageWorkflowHeaderProps = {
  projection: ClosingPackageProjection;
  isFetching?: boolean;
  onRefresh?: () => void;
};

/**
 * A1 Workflow Header — Design Freeze Variant B / Screen Spec §5.1.
 * Shows workflowState (not legacy phase), progress, cycle, lock.
 */
const ClosingPackageWorkflowHeader: React.FC<ClosingPackageWorkflowHeaderProps> = ({
  projection,
  isFetching = false,
  onRefresh,
}) => {
  const stateMeta = closingPackageWorkflowStateMeta(projection.workflowState);
  const progress = resolveClosingPackageWorkflowProgress(projection.workflowState);
  const approvalCycle = projection.workflowMetadata?.approvalCycle ?? 0;
  const locked = Boolean(projection.locked ?? projection.workflowMetadata?.locked);
  const reason = String(projection.workflowStateReason || '').trim();

  return (
    <div className="cp-workflow-header">
      <div className="cp-workflow-header__title-row">
        <Text strong className="cp-workflow-header__title">
          Комплект закрывающих документов
        </Text>
        {onRefresh ? (
          <Button
            size="small"
            type="text"
            icon={<ReloadOutlined />}
            loading={isFetching}
            onClick={onRefresh}
          >
            Обновить
          </Button>
        ) : null}
      </div>

      <div className="cp-workflow-header__status-row">
        <Tag color={stateMeta.color} className="cp-workflow-header__state-tag">
          {stateMeta.label}
        </Tag>

        <div className="cp-workflow-header__steps">
          <Steps
            size="small"
            current={progress.currentStepIndex}
            responsive={false}
            items={CLOSING_PACKAGE_PROGRESS_STEPS.map((step, index) => ({
              title: step.title,
              status: progress.stepStatuses[index],
            }))}
          />
        </div>

        <Space size={8} wrap className="cp-workflow-header__meta">
          {approvalCycle > 0 ? (
            <Tag className="cp-workflow-header__cycle">Цикл согласования: {approvalCycle}</Tag>
          ) : null}
          <Tooltip title={locked ? 'Состав заблокирован для изменений' : 'Редактирование открыто'}>
            <span className="cp-workflow-header__lock">
              {locked ? <LockOutlined /> : <UnlockOutlined />}
              <Text type="secondary">{locked ? 'Заблокирован' : 'Открыт'}</Text>
            </span>
          </Tooltip>
        </Space>
      </div>

      <div className="cp-workflow-header__progress">
        <Progress
          percent={progress.percent}
          size="small"
          showInfo
          strokeColor="#2563EB"
          trailColor="#E5E7EB"
          format={(percent) => `${percent}% · ${progress.currentGroupLabel}`}
        />
      </div>

      {reason ? (
        <Text type="secondary" className="cp-workflow-header__reason">
          {reason}
        </Text>
      ) : null}
    </div>
  );
};

export default ClosingPackageWorkflowHeader;
